import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clientsService } from '../../services/clientsService';
import { ClientResponse, ClientStatus, ClientOrderMinimalResponse, ClientPlatform } from '../../types/clients';
import { OrderStatus } from '../../types/orders';
import SendMessageToClientModal from '../modals/SendMessageToClientModal';
import ChangeClientStatusModal from '../modals/ChangeClientStatusModal';
import './Page.css';

const ClientDetails: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [client, setClient] = useState<ClientResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Orders state
  const [orders, setOrders] = useState<ClientOrderMinimalResponse[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersPage, setOrdersPage] = useState(0);
  const ordersPageSize = 10;

  useEffect(() => {
    if (clientId) {
      fetchClientDetails(parseInt(clientId));
      fetchClientOrders(parseInt(clientId), ordersPage);
    }
  }, [clientId, ordersPage]);

  const fetchClientDetails = async (id: number) => {
    setLoading(true);
    setError('');

    try {
      const response = await clientsService.getClient(id);

      if (response.success && response.data) {
        setClient(response.data);
      } else {
        setError(response.error || t('clientDetails.failedToFetch'));
      }
    } catch (error) {
      setError(t('clientDetails.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClientOrders = async (id: number, page: number) => {
    setOrdersLoading(true);

    try {
      const response = await clientsService.getClientOrders(id, page, ordersPageSize);

      if (response.success && response.data) {
        setOrders(response.data.orders || []);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch client orders:', error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!clientId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await clientsService.sendMessageToClient({
        clientId: parseInt(clientId),
        message: message
      });

      if (response.success) {
        setSuccessMessage(t('clientDetails.messageSentSuccess'));
        setIsMessageModalOpen(false);
      } else {
        setError(response.error || t('clientDetails.failedToSendMessage'));
      }
    } catch (error) {
      setError(t('clientDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleChangeStatus = async (newStatus: ClientStatus) => {
    if (!clientId) return;

    setStatusChangeLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await clientsService.changeClientStatus({
        clientId: parseInt(clientId),
        status: newStatus
      });

      if (response.success) {
        setSuccessMessage(t('clientDetails.statusChangeSuccess'));
        setIsStatusModalOpen(false);
        // Refresh client details to get updated status
        await fetchClientDetails(parseInt(clientId));
      } else {
        setError(response.error || t('clientDetails.failedToChangeStatus'));
      }
    } catch (error) {
      setError(t('clientDetails.networkError'));
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const getLocalizedStatus = (status: ClientStatus): string => {
    switch (status) {
      case ClientStatus.ACTIVE:
        return t('clients.statusActive');
      case ClientStatus.BLOCKED:
        return t('clients.statusBlocked');
      default:
        return status;
    }
  };

  const getLocalizedOrderStatus = (order: ClientOrderMinimalResponse): string => {
    if (order.problematicCompleteReason && order.orderStatus == OrderStatus.ORDER_COMPLETED) {
        return t('orders.status.completedWithProblem');
    }

    const status = order.orderStatus;
    switch (status) {
      case OrderStatus.ORDER_PLACED:
        return t('orders.status.orderPlaced');
      case OrderStatus.ORDER_ACCEPTED_BY_ADMIN:
        return t('orders.status.acceptedByAdmin');
      case OrderStatus.RESTAURANT_ACCEPTED_ORDER:
        return t('orders.status.restaurantAccepted');
      case OrderStatus.RESTAURANT_FINISHED_ORDER:
        return t('orders.status.readyForPickup');
      case OrderStatus.DRIVER_PICKED_UP_ORDER:
        return t('orders.status.inDelivery');
      case OrderStatus.DRIVER_DROPPED_OFF_ORDER:
        return t('orders.status.delivered');
      case OrderStatus.ORDER_COMPLETED:
        return t('orders.status.completed');
      case OrderStatus.ADMIN_CANCELLED_ORDER:
        return t('orders.status.cancelled');
      default:
        return status;
    }
  };

  const getOrderStatusColor = (order: ClientOrderMinimalResponse): string => {
    const status = order.orderStatus;
    switch (status) {
      case OrderStatus.ORDER_PLACED:
        return '#000000';
      case OrderStatus.ORDER_ACCEPTED_BY_ADMIN:
        return '#17a2b8';
      case OrderStatus.RESTAURANT_ACCEPTED_ORDER:
        return '#007bff';
      case OrderStatus.RESTAURANT_FINISHED_ORDER:
        return '#28a745';
      case OrderStatus.DRIVER_PICKED_UP_ORDER:
        return '#6c757d';
      case OrderStatus.DRIVER_DROPPED_OFF_ORDER:
        return '#20c997';
      case OrderStatus.ORDER_COMPLETED:
        return '#28a745';
      case OrderStatus.ADMIN_CANCELLED_ORDER:
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/order-details/${orderId}`);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="loading-container">{t('clientDetails.loadingClientDetails')}</div>
        </div>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/clients')} className="back-button">
            {t('clientDetails.backToClients')}
          </button>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="no-data">{t('clientDetails.clientNotFound')}</div>
          <button onClick={() => navigate('/clients')} className="back-button">
            {t('clientDetails.backToClients')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-with-back">
          <button onClick={() => navigate('/clients')} className="back-button">
            ← {t('clientDetails.backToClients')}
          </button>
          <div>
            <h1>{t('clientDetails.title', { clientName: client.name || t('clientDetails.defaultTitle') })}</h1>
            <p>{t('clientDetails.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Success Message */}
        {successMessage && (
          <div className="success-message" style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message" style={{
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Client Info */}
        <div className="detail-card full-width">
          <h3>{t('clientDetails.clientInformation')}</h3>
          <div className="info-grid">
            <div>
              <strong>{t('clientDetails.id')}:</strong> {client.id}
            </div>
            <div>
              <strong>{t('clientDetails.name')}:</strong> {client.name || <span style={{ color: '#999' }}>{t('clientDetails.notProvided')}</span>}
            </div>
            <div>
              <strong>{t('clientDetails.phone')}:</strong> {client.phone}
            </div>
            <div>
              <strong>{t('clients.platform')}:</strong>{' '}
              {client.platform ? (
                <span
                  style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    backgroundColor: client.platform === ClientPlatform.ANDROID ? '#3DDC84' : '#007AFF',
                    color: '#fff'
                  }}
                >
                  {client.platform}
                </span>
              ) : (
                <span style={{ color: '#999' }}>-</span>
              )}
            </div>
            <div>
              <strong>{t('clientDetails.statusLabel')}:</strong>{' '}
              <span
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  backgroundColor: client.status === ClientStatus.ACTIVE ? '#d4edda' : '#f8d7da',
                  color: client.status === ClientStatus.ACTIVE ? '#155724' : '#721c24'
                }}
              >
                {getLocalizedStatus(client.status)}
              </span>
            </div>
            <div>
              <strong>{t('clients.createdAt')}:</strong> {formatDate(client.createdAt)}
            </div>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="action-buttons">
          <h3>{t('clientDetails.adminActions')}</h3>
          <div className="action-buttons-grid">
            <button
              className="action-button accept"
              onClick={() => setIsMessageModalOpen(true)}
            >
              {t('clientDetails.sendMessage')}
            </button>
            <button
              className="action-button"
              onClick={() => setIsStatusModalOpen(true)}
            >
              {t('clientDetails.changeStatus')}
            </button>
          </div>
        </div>

        {/* Client Orders */}
        <div className="detail-card full-width" style={{ marginTop: '1.5rem' }}>
          <h3>{t('clientDetails.orders')}</h3>
          {ordersLoading ? (
            <div className="loading-container">{t('clientDetails.loadingOrders')}</div>
          ) : !orders || orders.length === 0 ? (
            <div className="no-data">{t('clientDetails.noOrders')}</div>
          ) : (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('orders.orderId')}</th>
                      <th>{t('orders.restaurant')}</th>
                      <th>{t('clientDetails.deliveryPrice')}</th>
                      <th>{t('orders.totalPrice')}</th>
                      <th>{t('common.status')}</th>
                      <th>{t('orders.createdAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders && orders.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => handleOrderClick(order.id)}
                        style={{ cursor: 'pointer' }}
                        className="clickable-row"
                      >
                        <td>#{order.id}</td>
                        <td>{order.restaurantName || '-'}</td>
                        <td>{order.deliveryPrice?.toLocaleString() || '0'} UZS</td>
                        <td><strong>{order.totalPrice?.toLocaleString() || '0'} UZS</strong></td>
                        <td>
                          <span
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              backgroundColor: getOrderStatusColor(order),
                              color: '#fff'
                            }}
                          >
                            {getLocalizedOrderStatus(order)}
                          </span>
                        </td>
                        <td>{formatDate(order.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!ordersLoading && orders.length > 0 && (
                <div className="pagination">
                  <button
                    className="pagination-button"
                    onClick={() => setOrdersPage(ordersPage - 1)}
                    disabled={ordersPage === 0}
                  >
                    {t('common.previous')}
                  </button>
                  <span className="pagination-info">{t('common.page')} {ordersPage + 1}</span>
                  <button
                    className="pagination-button"
                    onClick={() => setOrdersPage(ordersPage + 1)}
                    disabled={orders.length < ordersPageSize}
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <SendMessageToClientModal
        isOpen={isMessageModalOpen}
        onClose={() => setIsMessageModalOpen(false)}
        onSubmit={handleSendMessage}
        clientName={client.name || 'Client'}
        loading={modalLoading}
      />
      <ChangeClientStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onSubmit={handleChangeStatus}
        currentStatus={client.status}
        loading={statusChangeLoading}
      />
    </div>
  );
};

export default ClientDetails;
